import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { Category } from '../../types/Category';
import {
  categoryPickerNodeExpandable,
  categorySubtreeHasSelectableCompareTarget,
  contentCategoryMayHaveRows,
  contentCountForPickerDisplay,
  collectCompareContentTargetGroups,
  documentHasCompareTargets,
  findDefaultAddMatchingContentSourceIdAmongGroups,
  findCategoryInTree,
  findDocumentRootCategory,
  resolveCompareTargetForCategory,
  type CompareContentTarget,
} from '../../utils/CategoryUtils';
import {
  branchChildrenAreLoaded,
  categoryContentsAreLoaded,
} from '../../utils/categoryLoadState';
import {
  CompareContentMatchRule,
  CompareContentPreview,
  CompareContentScopeType,
  fetchCategoryForPane,
  mergeBranchFetchIntoTree,
  mergeContentsFetchIntoTree,
  previewCompareContent,
} from '../../services/CategoryService';

interface CompareContentDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCategory: Category | null;
  categoriesTree: Category[];
  onSelectContentIds: (contentIds: string[]) => void;
  /** Opens Split category with these rows in the first subcategory; the rest go to the second. */
  onSplitWithContentIds: (
    contentIds: string[],
    whichInFirst: 'matched' | 'unmatched',
  ) => void;
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (data && typeof data === 'object' && 'message' in data) {
      const msg = (data as { message?: unknown }).message;
      if (typeof msg === 'string' && msg.trim()) return msg;
    }
  }
  return fallback;
}

export type CategoryPickerTreeLabelProps = {
  category: Category;
  /** Row count when known; {@code null} means not loaded yet (show …, not 0). */
  contentCount: number | null;
  nameColor?: string;
  nameSuffix?: string;
};

/** Shared tree row: icon, name, (count) — LTR layout like English. */
export function CategoryPickerTreeLabel({
  category,
  contentCount,
  nameColor = 'text.primary',
  nameSuffix = '',
}: CategoryPickerTreeLabelProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        py: 0.25,
        width: '100%',
        minWidth: 0,
        direction: 'ltr',
      }}
    >
      {category.contentCategory ? (
        <DescriptionOutlinedIcon sx={{ fontSize: 18, opacity: 0.85, flexShrink: 0 }} />
      ) : (
        <FolderIcon sx={{ fontSize: 18, opacity: 0.85, flexShrink: 0 }} />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Typography
          component="span"
          variant="body2"
          dir="auto"
          sx={{
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: nameColor,
          }}
        >
          {category.name}
          {nameSuffix}
        </Typography>
        <Typography
          component="span"
          variant="body2"
          dir="ltr"
          sx={{ flexShrink: 0, color: 'text.secondary' }}
        >
          ({contentCount === null ? '…' : contentCount})
        </Typography>
      </Box>
    </Box>
  );
}

function collectAncestorCategoryIds(categoriesTree: Category[], categoryId: string): string[] {
  const ids: string[] = [];
  let parentId = findCategoryInTree(categoriesTree, categoryId)?.parentId;
  while (parentId) {
    ids.push(parentId);
    parentId = findCategoryInTree(categoriesTree, parentId)?.parentId;
  }
  return ids;
}

const CompareContentDialog: React.FC<CompareContentDialogProps> = ({
  open,
  onClose,
  sourceCategory,
  categoriesTree,
  onSelectContentIds,
  onSplitWithContentIds,
}) => {
  const [pickerTree, setPickerTree] = useState<Category[]>([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState<string[]>([]);
  const [selectedTargetCategoryId, setSelectedTargetCategoryId] = useState('');
  const [matchRule, setMatchRule] = useState<CompareContentMatchRule>('EQUIVALENT');
  const [preview, setPreview] = useState<CompareContentPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [splitIntoSubcategories, setSplitIntoSubcategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [treeLoadingIds, setTreeLoadingIds] = useState<Set<string>>(new Set());
  const previewRequestGenRef = useRef(0);
  const initGenRef = useRef(0);
  const pickerTreeRef = useRef<Category[]>([]);
  pickerTreeRef.current = pickerTree;

  const documentRoot = useMemo(
    () =>
      sourceCategory
        ? findDocumentRootCategory(pickerTree, sourceCategory.id)
        : null,
    [pickerTree, sourceCategory],
  );

  const hasTargets = useMemo(
    () => (sourceCategory ? documentHasCompareTargets(pickerTree, sourceCategory) : false),
    [pickerTree, sourceCategory],
  );

  const selectedTarget = useMemo((): CompareContentTarget | null => {
    if (!sourceCategory || !selectedTargetCategoryId) return null;
    const cat = findCategoryInTree(pickerTree, selectedTargetCategoryId);
    if (!cat) return null;
    return resolveCompareTargetForCategory(cat, sourceCategory.id);
  }, [sourceCategory, selectedTargetCategoryId, pickerTree]);

  const hydrateContentCategoryCounts = useCallback(async (categoryIds: string[]) => {
    const toLoad = categoryIds.filter((id) => {
      const live = findCategoryInTree(pickerTreeRef.current, id);
      return (
        !!live?.contentCategory &&
        !categoryContentsAreLoaded(live) &&
        (contentCategoryMayHaveRows(live) || live.id === sourceCategory?.id)
      );
    });
    if (toLoad.length === 0) return;

    setTreeLoadingIds((prev) => {
      const next = new Set(prev);
      toLoad.forEach((id) => next.add(id));
      return next;
    });
    try {
      const results = await Promise.all(
        toLoad.map(async (id) => {
          try {
            return { id, loaded: await fetchCategoryForPane(id) };
          } catch {
            return null;
          }
        }),
      );
      setPickerTree((prev) => {
        let next = prev;
        for (const result of results) {
          if (!result) continue;
          next = mergeContentsFetchIntoTree(next, result.id, result.loaded);
        }
        pickerTreeRef.current = next;
        return next;
      });
    } finally {
      setTreeLoadingIds((prev) => {
        const next = new Set(prev);
        toLoad.forEach((id) => next.delete(id));
        return next;
      });
    }
  }, [sourceCategory?.id]);

  const ensurePickerNodeLoaded = useCallback(
    async (categoryId: string) => {
      const live = findCategoryInTree(pickerTreeRef.current, categoryId);
      if (!live) return null;
      if (live.contentCategory) {
        if (categoryContentsAreLoaded(live)) return live;
        setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
        try {
          const loaded = await fetchCategoryForPane(categoryId);
          setPickerTree((prev) => {
            const next = mergeContentsFetchIntoTree(prev, categoryId, loaded);
            pickerTreeRef.current = next;
            return next;
          });
          return loaded;
        } finally {
          setTreeLoadingIds((prev) => {
            const next = new Set(prev);
            next.delete(categoryId);
            return next;
          });
        }
      }
      if (branchChildrenAreLoaded(live)) return live;
      setTreeLoadingIds((prev) => new Set(prev).add(categoryId));
      try {
        const loaded = await fetchCategoryForPane(categoryId);
        setPickerTree((prev) => {
          const next = mergeBranchFetchIntoTree(prev, categoryId, loaded);
          pickerTreeRef.current = next;
          return next;
        });
        const contentIds = (loaded.children ?? [])
          .filter(
            (c) =>
              c.contentCategory &&
              (contentCategoryMayHaveRows(c) || c.id === sourceCategory?.id),
          )
          .map((c) => c.id);
        if (contentIds.length > 0) {
          void hydrateContentCategoryCounts(contentIds);
        }
        return loaded;
      } finally {
        setTreeLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(categoryId);
          return next;
        });
      }
    },
    [hydrateContentCategoryCounts, sourceCategory?.id],
  );

  const refreshPreview = useCallback(
    async (
      targetId: string,
      scopeType: CompareContentScopeType,
      scopeCategoryId: string,
      rule: CompareContentMatchRule,
    ) => {
      if (!targetId || !scopeCategoryId) {
        setPreview(null);
        setError(null);
        return;
      }
      const requestGen = ++previewRequestGenRef.current;
      setPreviewLoading(true);
      setError(null);
      try {
        const data = await previewCompareContent(
          targetId,
          scopeType,
          scopeCategoryId,
          rule,
        );
        if (requestGen !== previewRequestGenRef.current) {
          return;
        }
        setPreview(data);
      } catch (err: unknown) {
        if (requestGen !== previewRequestGenRef.current) {
          return;
        }
        setPreview(null);
        setError(extractApiErrorMessage(err, 'Failed to compare content.'));
      } finally {
        if (requestGen === previewRequestGenRef.current) {
          setPreviewLoading(false);
        }
      }
    },
    [],
  );

  /** Select a compare target and load preview immediately (same as pre-lazy behavior). */
  const selectCompareTarget = useCallback(
    async (categoryId: string) => {
      if (!sourceCategory || !categoryId || categoryId === sourceCategory.id) return;
      if (categoryId.endsWith('__loading')) return;

      let cat = findCategoryInTree(pickerTreeRef.current, categoryId);
      if (!cat || (cat.contentCategory && !categoryContentsAreLoaded(cat))) {
        const loaded = await ensurePickerNodeLoaded(categoryId);
        cat = loaded ?? cat ?? findCategoryInTree(pickerTreeRef.current, categoryId);
      }
      if (!cat) return;

      const target = resolveCompareTargetForCategory(cat, sourceCategory.id);
      if (!target) return;

      setError(null);
      setSelectedTargetCategoryId(cat.id);
      setPreview(null);
      await refreshPreview(
        sourceCategory.id,
        target.kind === 'branch' ? 'BRANCH' : 'CONTENT_CATEGORY',
        cat.id,
        matchRule,
      );
    },
    [sourceCategory, ensurePickerNodeLoaded, refreshPreview, matchRule],
  );

  useEffect(() => {
    if (!open || !sourceCategory) {
      return;
    }

    const gen = ++initGenRef.current;
    const source = sourceCategory;
    const treeSnapshot = categoriesTree;

    setMatchRule('EQUIVALENT');
    setSplitIntoSubcategories(false);
    setPreview(null);
    setError(null);
    setSelectedTargetCategoryId('');
    setPreviewLoading(false);
    setTreeLoadingIds(new Set());

    void (async () => {
      let tree = treeSnapshot;
      setPickerTree(tree);
      pickerTreeRef.current = tree;

      const ancestorIds = collectAncestorCategoryIds(tree, source.id);
      const root = findDocumentRootCategory(tree, source.id);
      const initialExpanded = new Set<string>();
      if (root) initialExpanded.add(root.id);
      initialExpanded.add(source.id);
      ancestorIds.forEach((id) => initialExpanded.add(id));
      setExpandedNodeIds(Array.from(initialExpanded));

      for (const id of [...ancestorIds].reverse()) {
        if (gen !== initGenRef.current) return;
        const live = findCategoryInTree(tree, id);
        if (!live || live.contentCategory || branchChildrenAreLoaded(live)) continue;
        try {
          const loaded = await fetchCategoryForPane(id);
          tree = mergeBranchFetchIntoTree(tree, id, loaded);
          pickerTreeRef.current = tree;
          setPickerTree(tree);
        } catch {
          // User can still expand manually.
        }
      }
      if (gen !== initGenRef.current) return;

      // Hydrate counts for content categories visible on the expanded path.
      const expanded = new Set(initialExpanded);
      const contentIds: string[] = [];
      const walk = (cat: Category, parentExpanded: boolean) => {
        if (cat.contentCategory) {
          if (
            parentExpanded &&
            !categoryContentsAreLoaded(cat) &&
            (contentCategoryMayHaveRows(cat) || cat.id === source.id)
          ) {
            contentIds.push(cat.id);
          }
          return;
        }
        if (!expanded.has(cat.id)) return;
        for (const child of cat.children ?? []) {
          walk(child, true);
        }
      };
      for (const r of tree) {
        if (r.contentCategory) walk(r, true);
        else if (expanded.has(r.id)) {
          for (const child of r.children ?? []) walk(child, true);
        }
      }
      if (contentIds.length > 0) {
        await hydrateContentCategoryCounts(contentIds);
        tree = pickerTreeRef.current;
      }
      if (gen !== initGenRef.current) return;

      const groups = collectCompareContentTargetGroups(tree, source).map((g) => ({
        ancestorId: g.ancestorId,
        ancestorName: g.ancestorName,
        depth: g.depth,
        sources: g.targets
          .filter(
            (t): t is CompareContentTarget & { kind: 'contentCategory' } =>
              t.kind === 'contentCategory',
          )
          .map((t) => t.category),
      }));
      const initial =
        findDefaultAddMatchingContentSourceIdAmongGroups(groups, source.id) ?? '';
      if (initial && gen === initGenRef.current) {
        await selectCompareTarget(initial);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceCategory?.id]);

  // Re-run preview when the match rule changes for the current selection.
  useEffect(() => {
    if (!open || !sourceCategory || !selectedTargetCategoryId || !selectedTarget) {
      return;
    }
    void refreshPreview(
      sourceCategory.id,
      selectedTarget.kind === 'branch' ? 'BRANCH' : 'CONTENT_CATEGORY',
      selectedTarget.category.id,
      matchRule,
    );
    // Only when matchRule changes; selection already previews in selectCompareTarget.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchRule]);

  // Load row counts for visible content categories when expansion changes.
  useEffect(() => {
    if (!open || pickerTree.length === 0) return;
    const expanded = new Set(expandedNodeIds);
    const ids: string[] = [];
    const walk = (cat: Category, parentExpanded: boolean) => {
      if (cat.contentCategory) {
        if (
          parentExpanded &&
          !categoryContentsAreLoaded(cat) &&
          (contentCategoryMayHaveRows(cat) || cat.id === sourceCategory?.id)
        ) {
          ids.push(cat.id);
        }
        return;
      }
      if (!expanded.has(cat.id)) return;
      for (const child of cat.children ?? []) {
        walk(child, true);
      }
    };
    for (const root of pickerTree) {
      if (root.contentCategory) walk(root, true);
      else if (expanded.has(root.id)) {
        for (const child of root.children ?? []) walk(child, true);
      }
    }
    if (ids.length > 0) {
      void hydrateContentCategoryCounts(ids);
    }
  }, [open, pickerTree, expandedNodeIds, hydrateContentCategoryCounts, sourceCategory?.id]);

  const handleTreeSelect = (_event: React.SyntheticEvent, nodeIdInput: string | string[]) => {
    const nodeId = Array.isArray(nodeIdInput) ? nodeIdInput[0] : nodeIdInput;
    if (!nodeId) return;
    void selectCompareTarget(nodeId);
  };

  const handleNodeToggle = (_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpandedNodeIds(nodeIds);
    const newlyExpanded = nodeIds.filter((id) => !expandedNodeIds.includes(id));
    for (const id of newlyExpanded) {
      const cat = findCategoryInTree(pickerTreeRef.current, id);
      if (cat && !cat.contentCategory && !branchChildrenAreLoaded(cat)) {
        void ensurePickerNodeLoaded(id);
      } else if (cat && !cat.contentCategory && branchChildrenAreLoaded(cat)) {
        const contentIds = (cat.children ?? [])
          .filter(
            (c) =>
              c.contentCategory &&
              (contentCategoryMayHaveRows(c) || c.id === sourceCategory?.id),
          )
          .map((c) => c.id);
        if (contentIds.length > 0) {
          void hydrateContentCategoryCounts(contentIds);
        }
      }
    }
  };

  const handleApply = (which: 'matched' | 'unmatched') => {
    if (!preview || !sourceCategory) {
      return;
    }
    const ids =
      which === 'matched' ? preview.matchedContentIds : preview.unmatchedContentIds;
    if (splitIntoSubcategories) {
      onSplitWithContentIds(ids, which);
    } else {
      onSelectContentIds(ids);
    }
    onClose();
  };

  const renderCompareTreeNode = (category: Category): React.ReactNode | null => {
    if (!sourceCategory) return null;
    const isSource = category.id === sourceCategory.id;
    const isContent = !!category.contentCategory;
    // Hide empty content categories (source is kept as a disabled marker).
    if (isContent && !isSource && !contentCategoryMayHaveRows(category)) {
      return null;
    }
    const target = resolveCompareTargetForCategory(category, sourceCategory.id);
    const selectable = Boolean(target);
    if (
      !isSource &&
      !selectable &&
      !categorySubtreeHasSelectableCompareTarget(category, sourceCategory.id)
    ) {
      return null;
    }
    const expandable = categoryPickerNodeExpandable(category);
    const childrenLoaded = branchChildrenAreLoaded(category);
    const hasLoadedChildren = (category.children?.length ?? 0) > 0;
    const loading = treeLoadingIds.has(category.id);

    const contentCount = contentCountForPickerDisplay(category);
    const nameColor = isSource ? 'text.disabled' : selectable ? 'text.primary' : 'text.secondary';
    const label = (
      <Box
        onMouseDown={
          selectable
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
                void selectCompareTarget(category.id);
              }
            : undefined
        }
        sx={selectable ? { cursor: 'pointer', width: '100%' } : undefined}
      >
        <CategoryPickerTreeLabel
          category={category}
          contentCount={contentCount}
          nameColor={nameColor}
          nameSuffix={
            isSource
              ? ' (this category)'
              : loading
                ? ' (loading…)'
                : ''
          }
        />
      </Box>
    );

    // Content categories must not pass children — MUI shows a chevron for any child nodes.
    if (isContent) {
      return (
        <TreeItem
          key={category.id}
          nodeId={category.id}
          label={label}
          disabled={isSource}
        />
      );
    }

    return (
      <TreeItem
        key={category.id}
        nodeId={category.id}
        label={label}
        disabled={false}
      >
        {hasLoadedChildren
          ? category.children!.map((child) => renderCompareTreeNode(child)).filter(Boolean)
          : null}
        {expandable && !childrenLoaded && !hasLoadedChildren ? (
          <TreeItem
            nodeId={`${category.id}__loading`}
            label={
              <Typography variant="body2" color="text.secondary">
                Loading…
              </Typography>
            }
            disabled
          />
        ) : null}
      </TreeItem>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Compare content and select
        {sourceCategory?.name ? ` — ${sourceCategory.name}` : ''}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compare rows in <strong>{sourceCategory?.name ?? 'this category'}</strong> to another
          content category or branch. Click a folder (branch) or content category in the category structure.
          Selection applies only to rows in this category.
        </Typography>

        {!documentRoot ? (
          <Alert severity="warning">Could not load the category structure.</Alert>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.75 }}>
              Compare against
            </Typography>
            {!hasTargets && (
              <Alert severity="info" sx={{ mb: 1 }}>
                Expand folders to find another content category with rows, or a branch that
                contains content.
              </Alert>
            )}
            <Box
              sx={{
                maxHeight: 280,
                overflow: 'auto',
                border: (theme) => `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
                p: 0.5,
              }}
            >
              <TreeView
                key={`${documentRoot.id}-${sourceCategory?.id}`}
                defaultCollapseIcon={<ExpandMoreIcon />}
                defaultExpandIcon={<ChevronRightIcon />}
                expanded={expandedNodeIds}
                onNodeToggle={handleNodeToggle}
                selected={selectedTargetCategoryId || ''}
                onNodeSelect={handleTreeSelect}
              >
                {renderCompareTreeNode(documentRoot)}
              </TreeView>
            </Box>
          </Box>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Match rule
          </Typography>
          <RadioGroup
            value={matchRule}
            onChange={(e) => setMatchRule(e.target.value as CompareContentMatchRule)}
          >
            <FormControlLabel
              value="EQUIVALENT"
              control={<Radio />}
              label="Same subcase and text"
            />
            <FormControlLabel
              value="SUBCASE_ONLY"
              control={<Radio />}
              label="Same subcase only"
            />
          </RadioGroup>
        </FormControl>

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {selectedTargetCategoryId && (
          <Alert
            severity={
              previewLoading ? 'info' : preview && preview.matchedCount + preview.unmatchedCount > 0
                ? 'success'
                : 'warning'
            }
            icon={previewLoading ? <CircularProgress size={18} /> : undefined}
            sx={{ mb: 1 }}
          >
            {previewLoading
              ? 'Comparing…'
              : preview
                ? `${preview.matchedCount} matched, ${preview.unmatchedCount} unmatched (${preview.totalInSource} total in this category)`
                : 'No preview yet.'}
          </Alert>
        )}

        <FormControlLabel
          sx={{ mt: 1, alignItems: 'flex-start' }}
          control={
            <Checkbox
              checked={splitIntoSubcategories}
              onChange={(e) => setSplitIntoSubcategories(e.target.checked)}
              disabled={!selectedTargetCategoryId || !selectedTarget || !preview}
            />
          }
          label={
            <Box>
              <Typography variant="body2">Split into two subcategories</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                When checked, the button you click puts those rows in the first subcategory and the
                rest in the second (same as Split category with a selection). You name both
                subcategories next.
              </Typography>
            </Box>
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => handleApply('matched')}
          disabled={!preview || previewLoading}
        >
          {splitIntoSubcategories ? 'Split — matched in first' : 'Select matched'}
        </Button>
        <Button
          variant="contained"
          onClick={() => handleApply('unmatched')}
          disabled={!preview || previewLoading}
        >
          {splitIntoSubcategories ? 'Split — unmatched in first' : 'Select unmatched'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareContentDialog;
